import React, { useState, useEffect, useCallback, useRef } from 'react';
import GameScreen from './GameScreen';
import { createRemoteGame, playMove } from '../utils/api';
import { playWordFoundSound, playTileRevealSound, playSwapSound, playGameStartSound } from '../utils/sounds';
import { analyticsEvents } from '../utils/analytics';
// import { ThemeProvider } from 'styled-components'; // Import ThemeProvider
// import { lightTheme, darkTheme } from '../styles/theme'; // Import themes

// const themes = {
//   light: lightTheme,
//   dark: darkTheme,
// };

const RemoteGame = () => {
    const [gameState, setGameState] = useState(null);
    const [difficulty, setDifficulty] = useState('hard'); // Add difficulty state
    const [theme, setTheme] = useState('light');
    const [loading, setLoading] = useState(true); // For initial game load
    const [isProcessingMove, setIsProcessingMove] = useState(false); // For subsequent moves
    const [selectedTiles, setSelectedTiles] = useState([]);
    const [isCheckMode] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [animationState, setAnimationState] = useState({ lastFound: [], lastSwapped: [] });
    const gameWinTracked = useRef(false);

    const handleAiTurn = useCallback(async () => {
        setIsProcessingMove(true);
        // We send a null move because the backend knows it's the AI's turn
        // and will generate the move on its own.
        const response = await playMove(gameState, { action: 'ai_turn' }, difficulty);
        if (response && response.gameState) {
            setGameState(response.gameState);
            if (response.metadata && response.metadata.foundWords) {
                const allTiles = response.metadata.foundWords.flatMap(w => w.tiles);
                setAnimationState({ lastFound: allTiles });
            }
        }
        setIsProcessingMove(false);
    }, [gameState, difficulty]);

    // This effect will run whenever the game state (and thus the turn) changes.
    useEffect(() => {
        // If it's the AI's turn and the game is not over, trigger the AI's move.
        if (gameState && gameState.turn === 'ai' && !isProcessingMove) {
            handleAiTurn();
        }
    }, [gameState, isProcessingMove, handleAiTurn]); // Dependency array ensures this runs when gameState changes

    // Track game win
    useEffect(() => {
        if (gameState && gameState.game_over && gameState.winner && !gameWinTracked.current) {
            gameWinTracked.current = true;
            const playerWords = gameState.found_words?.player?.length || 0;
            const aiWords = gameState.found_words?.ai?.length || 0;
            analyticsEvents.gameWin({
                mode: 'single',
                winner: gameState.winner,
                playerWords,
                opponentWords: aiWords,
            });
        }
        // Reset tracking when game state resets
        if (!gameState || !gameState.game_over) {
            gameWinTracked.current = false;
        }
    }, [gameState]);

    useEffect(() => {
        const startGame = async () => {
            const initialState = await createRemoteGame();
            if (initialState) {
                // Ensure found_words is initialized properly
                initialState.found_words = initialState.found_words || { player: [], ai: [] };
                setGameState(initialState);
                
                // Play game start sound when remote game begins
                playGameStartSound();
                
                // Track game start
                analyticsEvents.gameStart('single');
            }
            setLoading(false);
        };
        startGame();
    }, []);

    const handleTileClick = async (row, col) => {
        if (isCheckMode) {
            const newSelectedTiles = [...selectedTiles];
            const selectedIndex = newSelectedTiles.findIndex(t => t.row === row && t.col === col);

            if (selectedIndex > -1) {
                newSelectedTiles.splice(selectedIndex, 1);
            } else {
                newSelectedTiles.push({ row, col });
            }
            setSelectedTiles(newSelectedTiles);
        } else {
            if (!gameState || gameState.turn !== 'player' || isProcessingMove) return;

            // Check if the tile is already revealed by the player
            const tile = gameState.grid[row][col];
            if (tile.revealedBy && tile.revealedBy.includes('player')) {
                // If a revealed tile is clicked, treat it as a swap selection
                handleSwapSelection(row, col);
                return;
            }

            // Reset animations on any new move
            setAnimationState({ lastFound: [] });

            // Play tile reveal sound
            playTileRevealSound();
            
            setIsProcessingMove(true);
            const response = await playMove(gameState, { action: 'reveal', row, col }, difficulty);

            if (response && response.gameState) {
                const updatedState = response.gameState;
                const metadata = response.metadata;

                // Ensure found_words is initialized properly
                updatedState.found_words = updatedState.found_words || { player: [], ai: [] };
                setGameState(updatedState);

                // Handle animations if a word was found
                if (metadata && (metadata.action === 'word_found' || (metadata.foundWords && metadata.foundWords.length > 0))) {
                    // Play word found sound
                    playWordFoundSound();
                    
                    const words = metadata.foundWords || [metadata];
                    const allTiles = words.flatMap(w => w.tiles);
                    
                    // Store the new grid state from backend (with replaced letters)
                    const newGridWithReplacements = updatedState.grid;
                    
                    // Temporarily keep the old grid for animation, but mark tiles as found
                    setAnimationState(prevState => ({ ...prevState, lastFound: allTiles }));
                    
                    // Calculate animation duration: base 1s + sequential delays (150ms per tile)
                    const maxDelay = (allTiles.length - 1) * 0.15;
                    const animationDuration = 1000 + (maxDelay * 1000);
                    
                    // After animation completes, update to the new grid with replaced letters
                    setTimeout(() => {
                        setGameState(prevState => ({
                            ...prevState,
                            grid: newGridWithReplacements,
                        }));
                        setAnimationState(prevState => ({ ...prevState, lastFound: [] }));
                    }, animationDuration + 100);
                    
                    // Don't update the grid immediately - let animation play first
                    setGameState(prevState => ({
                        ...updatedState,
                        grid: prevState.grid, // Keep old grid for animation
                    }));
                } else if (metadata && metadata.action === 'tiles_swapped') {
                    // Play magical swap sound
                    playSwapSound();
                    
                    // Handle swap animation
                    setAnimationState(prevState => ({ ...prevState, lastSwapped: metadata.tiles }));
                    setGameState(updatedState);
                } else {
                    // No animation needed, update normally
                    setGameState(updatedState);
                }
            } else {
                // This case handles situations where the response might be ill-formed
                // or the action didn't result in a valid new state from the backend.
                // console.error("[DEBUG] Invalid or empty response from server.");
            }
            setIsProcessingMove(false);
        }
    };

    const toggleTheme = () => {
        const newTheme = theme === 'light' ? 'dark' : 'light';
        setTheme(newTheme);
        // Analytics tracking is handled by ThemeToggleIcon component
    };

    const handleSwapSelection = (row, col) => {
        // If the clicked tile is the one already selected, cancel swap selection
        if (selectedTiles.length === 1 && selectedTiles[0].row === row && selectedTiles[0].col === col) {
            setSelectedTiles([]);
            return;
        }

        const tile = gameState.grid[row][col];
        if (!tile.revealedBy.includes('player')) {
            return; // Can only swap player's own tiles
        }
        const newSelectedTiles = [...selectedTiles, { row, col }];
        setSelectedTiles(newSelectedTiles);
        if (newSelectedTiles.length === 2) {
            setShowConfirmDialog(true);
        }
    };

    const handleConfirmSwap = async () => {
        if (selectedTiles.length === 2) {
            setIsProcessingMove(true);
            const [first, second] = selectedTiles;
            const swapMove = {
                action: 'swap_tiles',
                from: first,
                to: second
            };
            
            const response = await playMove(gameState, swapMove, difficulty);
            if (response && response.gameState) {
                const updatedState = response.gameState;
                const metadata = response.metadata;

                // Ensure found_words is initialized properly
                updatedState.found_words = updatedState.found_words || { player: [], ai: [] };

                // Handle animations if a word was found
                if (metadata && (metadata.action === 'word_found' || (metadata.foundWords && metadata.foundWords.length > 0))) {
                    const words = metadata.foundWords || [metadata];
                    const allTiles = words.flatMap(w => w.tiles);
                    
                    // Store the new grid state from backend (with replaced letters)
                    const newGridWithReplacements = updatedState.grid;
                    
                    // Temporarily keep the old grid for animation, but mark tiles as found
                    setAnimationState(prevState => ({ ...prevState, lastFound: allTiles }));
                    
                    // Calculate animation duration: base 1s + sequential delays (150ms per tile)
                    const maxDelay = (allTiles.length - 1) * 0.15;
                    const animationDuration = 1000 + (maxDelay * 1000);
                    
                    // After animation completes, update to the new grid with replaced letters
                    setTimeout(() => {
                        setGameState(prevState => ({
                            ...prevState,
                            grid: newGridWithReplacements,
                        }));
                        setAnimationState(prevState => ({ ...prevState, lastFound: [] }));
                    }, animationDuration + 100);
                    
                    // Don't update the grid immediately - let animation play first
                    setGameState(prevState => ({
                        ...updatedState,
                        grid: prevState.grid, // Keep old grid for animation
                    }));
                } else if (metadata && metadata.action === 'tiles_swapped') {
                    // Play magical swap sound
                    playSwapSound();
                    
                    // Handle swap animation
                    setAnimationState(prevState => ({ ...prevState, lastSwapped: metadata.tiles }));
                    setGameState(updatedState);
                } else {
                    // No animation needed, update normally
                    setGameState(updatedState);
                }
            }
            setIsProcessingMove(false);
        }
        setShowConfirmDialog(false);
        setSelectedTiles([]);
    };

    const handleCancelSwap = () => {
        setShowConfirmDialog(false);
        setSelectedTiles([]);
    };

    const handleConfirmCheckWord = async () => {
        // This is where you would send the selected word to the backend
    };

    const handleCancelCheckWord = () => {
        setSelectedTiles([]);
    };

    const handlers = {
        handleTileClick: (row, col) => {
            // The main handleTileClick now manages swap selection,
            // so we can just call it directly.
            handleTileClick(row, col);
        },
        handleConfirmSwap,
        handleCancelSwap,
        handleDifficultyChange: (e) => setDifficulty(e.target.value),
        handleConfirmCheckWord,
        handleCancelCheckWord,
    };

    if (loading || !gameState) {
        return <div>Loading remote game...</div>;
    }

    if (!gameState) {
        return <div>Error: Could not start the game.</div>;
    }
    
    // This mapping ensures GameScreen gets the data in the format it expects
    const screenGameState = {
        grid: gameState.grid,
        // playerScore: gameState.scores.player,  // REMOVED: No more scoring, just count words
        // aiScore: gameState.scores.ai,  // REMOVED: No more scoring, just count words
        playerFoundWords: gameState.found_words.player,
        aiFoundWords: gameState.found_words.ai,
        playerTurn: gameState.turn === 'player',
        selectedTiles: selectedTiles,
        isCheckMode: isCheckMode,
        animationState: animationState,
        showConfirmDialog: showConfirmDialog,
        isProcessingMove: isProcessingMove,
        aiDifficulty: difficulty,
        message: 'Find the hidden words!',
        gameOver: gameState.game_over,
        winner: gameState.winner,
    };

    return (
        <>
            <GameScreen
                theme={theme}
                toggleTheme={toggleTheme}
                gameState={screenGameState}
                handlers={handlers}
                setAnimationState={setAnimationState}
                isMultiplayer={false}
                showCheckButton={true}
            />
        </>
    );
};

export default RemoteGame; 